/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { humanCompare } from '@app/common/utils/human-compare';
import { ClusterInfo } from '@shared/types/cluster';
import { NEBULA_TEAM_ID, Team, TeamAuthority, TeamId, TeamMember, TeamType } from '@shared/types/team';

export const isTeamOwner = (teamMember: TeamMember) => teamMember.authorities.includes(TeamAuthority.owner);
const teamOwners = (team: Team): TeamMember[] => team.members.filter(isTeamOwner);
export const isMe = (team: Team, email: string) => getUserByEmail(team, email)?.isMe ?? false;
const PersonalAndNonSharedClusterTeamsFilter = (team: Team) =>
  !(team.type === TeamType.personal || (isClusterTeam(team) && team.members.length === 1));

export const getUserByEmail = (team: Team, email: string) => team.members.find((user) => user.email === email);
export const skipPersonalAndNonSharedClusterTeams = (teams: Team[]): Team[] =>
  teams.filter(PersonalAndNonSharedClusterTeamsFilter);
export const skipPersonalAndClusterTeams = (teams: Team[]): Team[] =>
  teams.filter((team) => ![TeamType.personal, TeamType.cluster].includes(team.type));
export const getPersonalTeam = (teams: Team[]): Team | undefined =>
  teams.find((team) => team.type === TeamType.personal);
export const isTeamOwnedByUser = (team: Team, email: string): boolean =>
  teamOwners(team).some((member) => member.email === email);
export const isTeamOwnedByMe = (team: Team): boolean => teamOwners(team).some((member) => member.isMe);
export const canILeaveTeam = (team: Team) => (isTeamOwnedByMe(team) ? teamOwners(team).length > 1 : true);
export const getTeamById = (teams: Team[], teamId: TeamId): Team | undefined =>
  teams.find((team) => team.id === teamId);
export const sortTeamsByName = (teams: Team[]): Team[] => [...teams].sort((a, b) => humanCompare(a.name, b.name));
export const canRemoveUser = (team: Team, email: string) =>
  isTeamOwnedByMe(team) ? (isMe(team, email) ? canILeaveTeam(team) : true) : false;
export const chooseDefaultTeam = (teams: Team[], skipPersonal = true): Team | null =>
  skipPersonal ? (skipPersonalAndClusterTeams(sortTeamsByName(teams))[0] ?? null) : (getPersonalTeam(teams) ?? null);
export const firstTeamClusterBelongsTo = (teams: Team[], cluster: ClusterInfo): Team | null =>
  teams.find((team) => cluster.teamIds.includes(team.id)) ?? null;
export const isClusterPersonal = (teams: Team[], cluster: ClusterInfo): boolean => {
  const personalTeam = getPersonalTeam(teams);
  return personalTeam ? cluster.teamIds.includes(personalTeam.id) : false;
};
export const userFacingTeamName = (team: Team): 'Personal clusters' | string =>
  team.type === TeamType.personal ? 'Personal clusters' : team.name;
export const clusterTeams = (cluster: ClusterInfo, teams: Team[]): Team[] => {
  const clusterTeamIds = new Set(cluster.teamIds);
  return teams.filter((team) => clusterTeamIds.has(team.id));
};
export const isClusterInNebulaTeam = (cluster: ClusterInfo) => cluster.teamIds.includes(NEBULA_TEAM_ID);
export const isMeInNebulaTeam = (teams: Team[]) => !!getTeamById(teams, NEBULA_TEAM_ID);

export const isClusterTeam = (team: Team) => team.type === TeamType.cluster;
export const isSharedClusterTeam = (team: Team) =>
  isClusterTeam(team) && !!team.members.filter((m) => !isTeamOwner(m)).length;
export const isClusterTeamOwnedByMe = (team: Team) => isClusterTeam(team) && isTeamOwnedByMe(team);
